import { Injectable } from '@angular/core';
import { gql } from 'apollo-angular';
import { map, Observable } from 'rxjs';
import {
  IComment,
  ICommentActionEvent,
} from 'src/app/models_new/types/comment';
import { Role } from 'src/app/models_new/types/role';
import { ClientApiService } from './client-api.service';

export enum SimulationType {
  PRIVATE = 'private',
  PUBLIC = 'public',
  OPEN = 'open',
}

@Injectable({
  providedIn: 'root',
})
export class CommentApiService {
  constructor(private clientApi: ClientApiService) {}
  public getComments(
    simulation_id: string,
    simulation_mode?: SimulationType
  ): Observable<IComment[]> {
    // For normal simulations, both Public and private.
    const q = gql`
      subscription getComments {
        simulation_comment(
          order_by: { created_at: desc }
          where: {
            simulation_id: { _eq: "${simulation_id}" }
          }
        ) {
          comment
          created_at
          updated_at
          id
          parent_comment
          metadata
          ${simulation_mode === SimulationType.PRIVATE ? 'owner_id' : ''}
        }
      }
    `;

    // For Open simulations.
    const oq = gql`
      subscription getOpenComments {
        open_simulation_comment(
          order_by: { created_at: desc }
          where: {
            open_simulation_id: { _eq: "${simulation_id}" }
          }
        ) {
          comment
          created_at
          updated_at
          id
          parent_comment
          metadata
        }
      }
    `;
    return this.clientApi
      .useClient(
        simulation_mode === SimulationType.PRIVATE ? 'user' : 'public',
        'ws'
      )
      .subscribe<any>({
        query: simulation_mode === 'open' ? oq : q,
      })
      .pipe(
        map((data) =>
          simulation_mode === 'open'
            ? data.data.open_simulation_comment
            : data.data.simulation_comment
        )
      );
  }

  public deleteCommentById(comment_id: string, role?: Role): Observable<any> {
    const q = gql`
      mutation deleteSimulationCommentByPk($id: uuid!) {
        delete_simulation_comment_by_pk(id: $id) {
          id
        }
      }
    `;

    const variables = {
      id: comment_id,
    };
    return this.clientApi
      .useClient(role ?? 'user', 'ws')
      .subscribe<any>({
        query: q,
        variables: variables,
      })
      .pipe(map((data) => data.data.delete_simulation_comment_by_pk));
  }

  public insertComment(
    comment: ICommentActionEvent['comment_reply'],
    simulation_id: string,
    simulation_mode: SimulationType,
    metadata: object
  ): Observable<any> {
    let commentMutation;
    switch (simulation_mode) {
      case SimulationType.PRIVATE:
      case SimulationType.PUBLIC:
        commentMutation = comment.parent_id
          ? gql`
              mutation insertSimulationComment(
                $comment: String!
                $parentId: uuid!
                $simulationId: uuid!
                $metadata: json
              ) {
                insert_simulation_comment_one(
                  object: {
                    comment: $comment
                    parent_comment: $parentId
                    simulation_id: $simulationId
                    metadata: $metadata
                  }
                ) {
                  simulation_id
                }
              }
            `
          : gql`
              mutation insertSimulationComment(
                $comment: String!
                $simulationId: uuid!
                $metadata: json
              ) {
                insert_simulation_comment_one(
                  object: {
                    comment: $comment
                    simulation_id: $simulationId
                    metadata: $metadata
                  }
                ) {
                  simulation_id
                }
              }
            `;
        break;
      case SimulationType.OPEN:
        // TODO: Update OPEN case when new table created for open-sim-comments.
        commentMutation = comment.parent_id
          ? gql`
              mutation insertOpenSimulationComment(
                $comment: String!
                $parentId: uuid!
                $simulationId: uuid!
                $metadata: json
              ) {
                insert_open_simulation_comment(
                  objects: {
                    comment: $comment
                    parent_comment: $parentId
                    open_simulation_id: $simulationId
                    metadata: $metadata
                  }
                ) {
                  affected_rows
                }
              }
            `
          : gql`
              mutation insertOpenSimulationComment(
                $comment: String!
                $simulationId: uuid!
                $metadata: json
              ) {
                insert_open_simulation_comment(
                  objects: {
                    comment: $comment
                    open_simulation_id: $simulationId
                    metadata: $metadata
                  }
                ) {
                  affected_rows
                }
              }
            `;
        break;
    }

    const variables = {
      comment: comment.comment,
      parentId: comment.parent_id,
      simulationId: simulation_id,
      metadata: metadata,
    };
    return this.clientApi
      .useClient(
        simulation_mode == SimulationType.PUBLIC ||
          simulation_mode == SimulationType.OPEN
          ? 'public'
          : 'user'
      )
      .mutate<any>({
        mutation: commentMutation,
        variables: variables,
      })
      .pipe(
        map((data) => {
          return data.data;
        })
      );
  }

  public updateCommentById(
    comment_id: string,
    comment: string
  ): Observable<any> {
    const q = gql`
      mutation deleteSimulationCommentByPk {
        update_simulation_comment_by_pk(
          pk_columns: { id: "${comment_id}" }
          _set: { comment: "${comment}" }
        ) {
          id
        }
      }
    `;

    return this.clientApi
      .useClient('user', 'ws')
      .subscribe<any>({
        query: q,
      })
      .pipe(map((data) => data.data.update_simulation_comment_by_pk.id));
  }
}
